import {Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild} from '@angular/core';
import { Subject, combineLatest } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';
import { Product } from '../../models/product.model';
import { User } from '../../models/user.model';
import { ActivatedRoute } from '@angular/router';
import { ProductService } from 'app/my-products/shared/product.service';
import { ProductsCacheService } from '../../my-products/shared/products-cache.service';
import { PagerService } from '../../pager/pager.service';
import { SortPipe } from '../../my-products/shared/sort.pipe';
import { AuthService } from '../../account/shared/auth.service';
import { UiService } from '../../my-products/shared/ui.service';
import {SidebarFiltersComponent} from './sidebar-filters/sidebar-filters.component';

interface FilterState {
  category: string | null;
  subCategories: { name: string; selected: boolean; count?: number }[];
  discounts: { name: string; selected: boolean; count?: number }[];
  brands: { name: string; selected: boolean; count?: number }[];
  filteredBrands: { name: string; selected: boolean; count?: number }[];
}

@Component({
  selector: 'app-my-list-product',
  templateUrl: './my-list-product.component.html',
  styleUrls: ['./my-list-product.component.scss'],
})
export class MyListProductComponent implements OnInit, OnDestroy {
  unsubscribe$ = new Subject<void>();
  products: Product[] = [];
  user: User | null = null;
  productsLoading: boolean = false;
  errorMessage: string | null = null;
  category: string | null = null;


  displayedProducts: Product[] = [];
  loadMoreLoading: boolean = false;
  batchSize: number = 36; // Number of products per batch
  currentIndex: number = 0;

  showBackToTop: boolean = false;
  @ViewChild('productGrid') productGrid!: ElementRef; // Reference to the product grid
  private observer: IntersectionObserver | null = null;

  @ViewChild(SidebarFiltersComponent) filterSidebar!: SidebarFiltersComponent;

  constructor(
    private productService: ProductService,
    private productsCacheService: ProductsCacheService,
    private pagerService: PagerService,
    private sortPipe: SortPipe,
    private authService: AuthService,
    public uiService: UiService,
    private route: ActivatedRoute
  ) {}

  ngOnInit() {

    this.authService.user.pipe(takeUntil(this.unsubscribe$)).subscribe((user) => {
      this.user = user;
    });

    this.uiService.sorting$.pipe(takeUntil(this.unsubscribe$)).subscribe(sortBy => {
      this.sortPipe.transform(
        this.products,
        sortBy.replace(':reverse', ''),
        sortBy.endsWith(':reverse')
      );
      this.updateDisplayedProducts();
    });  }

  updateDisplayedProducts() {
    const endIndex = this.currentIndex + this.batchSize;
    this.displayedProducts = this.products.slice(0, endIndex);
  }

  loadMore() {
    if (this.currentIndex >= this.products.length) return;
    this.loadMoreLoading = true;
    this.currentIndex += this.batchSize;
    this.updateDisplayedProducts();
    this.loadMoreLoading = false;
  }

  @HostListener('window:scroll', [])
  onWindowScroll() {
    this.showBackToTop = window.pageYOffset > 300; // Show after 300px
  }

  scrollToTop() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }




  refreshProducts() {
    const cacheKey = `products_${this.category || 'all'}`;
    this.productService.getProductsByCategorie(this.category || '')
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(products => {
        this.products = products;
      //  this.productsCacheService.set(cacheKey, this.products);
        this.updateCounts();
        this.updateDisplayedProducts();
      });
  }

  onFilterChange(filterState: FilterState) {
    console.log('onFilterChange called with filterState:', filterState);
    this.productsLoading = true;
    this.errorMessage = null;
    this.currentIndex = 0;
    this.displayedProducts = [];

    const filters = this.buildFilters(filterState);
    console.log('filters :', filters);
    this.productService.searchProductsOptimized(filters)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (productFilters) => {
          combineLatest(
            productFilters.map(pf => this.productService.getProduct(pf.id.toString()))
          ).pipe(
            takeUntil(this.unsubscribe$),
            tap(products => {
              this.products = products.filter((p): p is Product => p !== null);
              this.updateCounts();
              this.updateCounts();
              this.updateDisplayedProducts();
              this.productsLoading = false;

             // this.setPage(this.currentPagingPage || 1);
              this.productsLoading = false;
            })
          ).subscribe();
        },
        error: (err) => {
          this.errorMessage = 'Failed to load filtered products. Please try again.';
          this.productsLoading = false;
        }
      });
  }

  buildFilters(filterState: FilterState) {
    const selectedCategory = filterState.category;
    const selectedSubCategories = filterState.subCategories.filter(sc => sc.selected).map(sc => sc.name);
    const selectedBrands = filterState.brands.filter(b => b.selected).map(b => b.name);

    return {
      category: selectedCategory || undefined,
      famille: selectedSubCategories.length ? selectedSubCategories[0] : undefined,
      marque: selectedBrands.length ? selectedBrands[0] : undefined,
   //   limitToFirst: 100
    };
  }

  updateCounts(): void {
    this.filterSidebar.subCategories.forEach(category => {
        category.count = this.products.filter(p =>
          p.sousFamilles && p.sousFamilles[category.name] === true
        ).length;

    });

    this.filterSidebar.discounts.forEach(discount => {
      if (discount.name.includes("Jusqu'à -30 %")) {
        discount.count = this.products.filter(p => p.reduction && p.reduction <= 30).length;
      } else if (discount.name.includes('de 31 % à -50 %')) {
        discount.count = this.products.filter(p => p.reduction && p.reduction > 30 && p.reduction <= 50).length;
      } else if (discount.name.includes('plus de 50 %')) {
        discount.count = this.products.filter(p => p.reduction && p.reduction > 50).length;
      }
    });

    this.filterSidebar.brands.forEach(brand => {
      brand.count = this.products.filter(p => p.marque === brand.name).length;
    });
    this.filterSidebar.filteredBrands = [...this.filterSidebar.brands];
  }

  isNewProduct(date: string): boolean {
    const today = new Date();
    const productDate = new Date(date);
    const diffTime = Math.abs(today.getTime() - productDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 30;
  }

  toggleFilters(): void {
    this.uiService.toggleFilters();
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  ngAfterViewInit(): void {
    const sentinel = this.productGrid.nativeElement.querySelector('#sentinel');
    if (sentinel) {
      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && !this.productsLoading && !this.loadMoreLoading && this.currentIndex < this.products.length) {
            this.loadMore();
          }
        },
        { threshold: 0.1 }
      );
      this.observer.observe(sentinel);
    }
  }
}
